import React, { useEffect, useMemo, useState } from 'react';
import { Card, Checkbox, Divider, Typography, Spin } from 'antd';
import { useSelector } from 'react-redux';
import { sprayService } from '@iso/services';

const { Text } = Typography;

/**
 * Normalize PPE data from old (flat array) or new (object with preparation/spraying) format.
 * Lifted from the deleted PPERequiredModal so the inline checklist remains
 * backward-compatible with jobs created before the structured PPE shape.
 *
 * @param {Array|Object} ppeData
 * @returns {{ preparation: string[], spraying: string[] }}
 */
const normalizePPE = (ppeData) => {
  if (!ppeData) return { preparation: [], spraying: [] };
  if (ppeData.preparation || ppeData.spraying) {
    return {
      preparation: ppeData.preparation || [],
      spraying: ppeData.spraying || [],
    };
  }
  if (Array.isArray(ppeData)) {
    return { preparation: ppeData, spraying: [] };
  }
  return { preparation: [], spraying: [] };
};

const isPPEEmpty = (normalized) =>
  (!normalized.preparation || normalized.preparation.length === 0) &&
  (!normalized.spraying || normalized.spraying.length === 0);

/**
 * Inline PPE confirmation checklist — replaces the modal-based PPERequiredModal
 * used by the old kanban flow. Renders the PPE items for `phase`
 * ('preparation' or 'spraying') and reports the all-checked state up to the
 * parent via `onChange` so the page can gate its primary submit button.
 *
 * If the job has no PPE for the requested phase, falls back to the property's
 * spray-settings defaults (handles legacy jobs created before defaults were
 * configured).
 *
 * @param {Object} props
 * @param {Object} props.job  The spray job (must have ppe_requirements and property_id)
 * @param {'preparation'|'spraying'} props.phase
 * @param {(allChecked: boolean) => void} props.onChange
 * @param {string} [props.title]
 */
const PPEChecklist = ({ job, phase, onChange, title }) => {
  const propertyId = useSelector((state) => state.property?.activePropertyId) || job?.property_id;
  const [selected, setSelected] = useState([]);
  const [fallback, setFallback] = useState({ preparation: [], spraying: [] });
  const [usingFallback, setUsingFallback] = useState(false);
  const [fetchingFallback, setFetchingFallback] = useState(false);

  const normalized = useMemo(() => normalizePPE(job?.ppe_requirements), [job?.ppe_requirements]);

  // Fetch property defaults when the job has no PPE configured for this phase.
  useEffect(() => {
    if (!propertyId) return;
    if (!isPPEEmpty(normalized)) {
      setUsingFallback(false);
      setFallback({ preparation: [], spraying: [] });
      return;
    }
    let cancelled = false;
    setFetchingFallback(true);
    sprayService.getSettings(propertyId)
      .then((res) => {
        if (cancelled || !res?.settings) return;
        const prep = res.settings.ppe_defaults_preparation?.setting_value || [];
        const spray = res.settings.ppe_defaults_spraying?.setting_value || [];
        if (prep.length > 0 || spray.length > 0) {
          setFallback({ preparation: prep, spraying: spray });
          setUsingFallback(true);
        }
      })
      .catch((err) => {
        if (!cancelled) console.error('PPEChecklist: failed to load property defaults', err);
      })
      .finally(() => {
        if (!cancelled) setFetchingFallback(false);
      });
    return () => { cancelled = true; };
  }, [propertyId, normalized]);

  const effective = usingFallback ? fallback : normalized;
  const items = effective[phase] || [];

  // Bubble the all-checked state up to the parent so it can gate the submit
  // button. We re-broadcast whenever selection or item list changes.
  // `onChange` is deliberately omitted from the dep list — it's an event
  // emitter, not state, and including it would loop whenever a parent
  // passes an inline arrow handler (its identity changes every render).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!onChange) return;
    const allChecked = items.length === 0 || selected.length === items.length;
    onChange(allChecked);
  }, [selected, items]);

  const toggle = (item, checked) => {
    setSelected((prev) => (checked ? [...prev, item] : prev.filter((p) => p !== item)));
  };

  const headerTitle =
    title || (phase === 'preparation' ? 'PPE — Preparation' : 'PPE — Spraying');

  return (
    <Card title={headerTitle} style={{ marginBottom: 16 }}>
      {fetchingFallback ? (
        <Spin />
      ) : items.length === 0 ? (
        <Text type="secondary">
          No PPE requirements specified for this job, and no property defaults configured.
        </Text>
      ) : (
        <>
          {usingFallback && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontStyle: 'italic' }}>
              Using property default PPE list (no PPE was set on this job).
            </Text>
          )}
          {items.map((item) => (
            <div key={item} style={{ marginBottom: 8 }}>
              <Checkbox
                checked={selected.includes(item)}
                onChange={(e) => toggle(item, e.target.checked)}
              >
                {item}
              </Checkbox>
            </div>
          ))}
          <Divider style={{ margin: '12px 0' }} />
          <Text type={selected.length === items.length ? 'success' : 'warning'}>
            {selected.length} / {items.length} confirmed
          </Text>
        </>
      )}
    </Card>
  );
};

export default PPEChecklist;
